import { useCallback, useEffect, useRef, useState } from "react";
import { isPendingKitchenJob } from "./pendingKitchenDecisions";

export const KITCHEN_ALERT_SOUND_URL = "https://actions.google.com/sounds/v1/alarms/alarm_clock.ogg";

/** One persistent player for the whole KDS, not one player per tab/card. */
export default function useKitchenOrderAlert({ jobs = [], scope, loading, error }) {
  const audioRef = useRef(null);
  const state = useRef({ scope, seen: new Set(), pending: new Set(), token: 0, blocked: false });
  const [needsInteraction, setNeedsInteraction] = useState(false);
  const stop = useCallback(() => {
    const current = state.current;
    current.token += 1;
    current.blocked = false;
    setNeedsInteraction(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);
  const play = useCallback(() => {
    if (!audioRef.current || !state.current.pending.size) return;
    const token = ++state.current.token;
    const blocked = () => {
      if (state.current.token !== token) return;
      state.current.blocked = true;
      setNeedsInteraction(true);
    };
    try {
      audioRef.current.currentTime = 0;
      const result = audioRef.current.play();
      result?.then?.(() => {
        if (state.current.token === token) {
          state.current.blocked = false;
          setNeedsInteraction(false);
        }
      }, blocked);
    } catch (_) { blocked(); }
  }, []);
  const acknowledge = useCallback((jobIds = []) => {
    (jobIds || []).forEach((id) => {
      state.current.seen.add(String(id));
      state.current.pending.delete(String(id));
    });
    // A decision acknowledges the current audible notification, not other orders.
    // The next genuinely new pending order starts a new alert.
    stop();
  }, [stop]);

  useEffect(() => {
    if (state.current.scope !== scope) {
      stop();
      state.current = { scope, seen: new Set(), pending: new Set(), token: state.current.token, blocked: false };
    }
    // Loading and failed reads are not evidence that orders have been handled.
    if (loading || error) return;
    const pending = new Set(jobs.filter(isPendingKitchenJob).map((job) => String(job.jobId)));
    const hasNew = [...pending].some((id) => !state.current.seen.has(id));
    const handled = [...state.current.pending].some((id) => !pending.has(id));
    if (handled || !pending.size) stop();
    state.current.pending = pending;
    pending.forEach((id) => state.current.seen.add(id));
    if (hasNew) play();
  }, [jobs, scope, loading, error, stop, play]);

  useEffect(() => {
    const unlock = () => { if (state.current.blocked) play(); };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      state.current.token += 1;
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    };
  }, [play]);
  return { audioRef, acknowledge, needsInteraction, enableSound: play };
}
